import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Supplier } from "../types";
import { turkishIncludes } from "../utils/formatters";
import { firmHue, firmInitials } from "./FirmPicker";
import "./FirmPicker.css";
import "../pages/PurchaseOrders.css";

/**
 * Tedarikçi seçici — firma seçicinin (fp-*) kardeşi, tekli seçim.
 * Modal veri çekmez; tedarikçi listesini sayfa verir. Formda ve araç çubuğu süzgecinde kullanılır.
 */

export interface SupplierPickerModalProps {
  open: boolean;
  onClose: () => void;
  suppliers: Supplier[];
  selectedId?: string | null;
  onSelect: (s: Supplier | null) => void;
  /** Süzgeçte true: pasifler de listelenir. Formda false: yalnız aktifler (seçili pasif yine görünür). */
  includeInactive?: boolean;
  /** Verilirse ilk satır bu etiketle "seçim yok" olur (ör. "Tüm tedarikçiler"). */
  nullLabel?: string;
  title?: string;
  /** "Yeni tedarikçi" düğmesi. Seçiciyi kapatmak bunun işidir: onClose çağrılmaz, odak açılan karta geçer (alana geri dönmez). */
  onCreate?: () => void;
}

export interface SupplierPickerFieldProps extends Omit<SupplierPickerModalProps, "open" | "onClose" | "selectedId" | "onSelect"> {
  value?: string | null;
  onChange: (s: Supplier | null) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  allowClear?: boolean;
  variant?: "form" | "toolbar";
  id?: string;
  autoFocus?: boolean;
}

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>,
  chev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17.5" cy="18" r="1.6" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
};

const matches = (s: Supplier, q: string) => {
  const terms = q.trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = [s.name, s.code, s.contactPerson, s.taxNumber, s.phone].filter(Boolean).map(String);
  return terms.every((t) => hay.some((h) => turkishIncludes(h, t)));
};
const hueOf = (s: Supplier) => firmHue(s.code || s.name);
let seq = 0;

export const SupplierPickerModal: React.FC<SupplierPickerModalProps> = ({ open, onClose, suppliers, selectedId, onSelect, includeInactive, nullLabel, title = "Tedarikçi seç", onCreate }) => {
  const uid = useRef(`sp${++seq}`).current;
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ(""); setCursor(0);
    const t = window.setTimeout(() => searchRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  const source = useMemo(
    () => suppliers.filter((s) => includeInactive || s.isActive || s.supplierId === selectedId),
    [suppliers, includeInactive, selectedId]);
  const filtered = useMemo(
    () => source.filter((s) => matches(s, q)).sort((a, b) => a.name.localeCompare(b.name, "tr")),
    [source, q]);
  const rowCount = filtered.length + (nullLabel ? 1 : 0);
  useEffect(() => { setCursor(nullLabel && q.trim() ? 1 : 0); }, [q, nullLabel]);
  useEffect(() => { listRef.current?.querySelector<HTMLElement>(`[data-cursor="${cursor}"]`)?.scrollIntoView({ block: "nearest" }); }, [cursor]);

  const pick = (s: Supplier | null) => { onSelect(s); onClose(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); onClose(); return; }
    if (e.target !== searchRef.current) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(rowCount - 1, c + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (nullLabel && cursor === 0) return pick(null);
      const s = filtered[nullLabel ? cursor - 1 : cursor];
      if (s) pick(s);
    }
  };

  if (!open) return null;
  let idx = nullLabel ? 1 : 0;
  return createPortal(
    <div className="fp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
      <div className="fp-modal" role="dialog" aria-modal="true" aria-labelledby={`${uid}-t`} onMouseDown={(e) => e.stopPropagation()}>
        <header className="fp-head">
          <div>
            <span className="fp-kicker">Tedarikçi</span>
            <h2 id={`${uid}-t`}>{title}</h2>
            <p className="fp-sub">{source.length} tedarikçi</p>
          </div>
          <button type="button" className="fp-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </header>
        <div className="fp-tools">
          <div className="fp-search">{I.search}
            <input ref={searchRef} placeholder="Ad, kod, yetkili veya vergi no ara" value={q} onChange={(e) => setQ(e.target.value)} />
            {q && <button type="button" className="fp-ib" aria-label="Temizle" onClick={() => { setQ(""); searchRef.current?.focus(); }}>{I.close}</button>}
          </div>
          {onCreate && <button type="button" className="fp-btn primary" onClick={onCreate}>{I.plus} Yeni tedarikçi</button>}
        </div>
        <div className="fp-list" ref={listRef} role="listbox" aria-label="Tedarikçiler">
          {nullLabel && (
            <button type="button" role="option" aria-selected={!selectedId} tabIndex={-1} className={`fp-row null ${cursor === 0 ? "cur" : ""} ${!selectedId ? "sel" : ""}`} data-cursor={0} onMouseEnter={() => setCursor(0)} onClick={() => pick(null)}>
              <span className="fp-mark null">{I.truck}</span>
              <span className="fp-text"><b>{nullLabel}</b><small>süzgeç uygulanmaz</small></span>
              {!selectedId && <span className="fp-check">{I.check}</span>}
            </button>
          )}
          {filtered.length === 0 && (
            source.length === 0
              ? <div className="fp-blank"><span className="fp-blank-ico">{I.inbox}</span><b>Henüz tedarikçi yok</b>Sipariş verebilmek için önce tedarikçi kartı açın.{onCreate && <button type="button" className="fp-btn primary" onClick={onCreate}>{I.plus} Yeni tedarikçi</button>}</div>
              : <div className="fp-blank"><span className="fp-blank-ico">{I.inbox}</span><b>"{q}" için tedarikçi bulunamadı</b>Aramayı değiştirin.</div>
          )}
          {filtered.map((s) => {
            const i = idx++; const sel = s.supplierId === selectedId;
            return (
              <button type="button" key={s.supplierId} role="option" aria-selected={sel} tabIndex={-1} className={`fp-row ${cursor === i ? "cur" : ""} ${sel ? "sel" : ""} ${s.isActive ? "" : "off"}`} data-cursor={i} onMouseEnter={() => setCursor(i)} onClick={() => pick(s)}>
                <span className={`fp-mark ${s.isActive ? "" : "off"}`} style={{ ["--fp-hue" as any]: hueOf(s) }}>{firmInitials(s.name)}</span>
                <span className="fp-text">
                  <b>{s.name}{s.code && <span className="fp-code">{s.code}</span>}{!s.isActive && <span className="fp-badge off">Pasif</span>}</b>
                  <small>{[s.contactPerson, s.phone].filter(Boolean).join(" · ") || "İletişim bilgisi yok"}</small>
                </span>
                <span className="fp-side"><b>{s.paymentTermDays} gün</b><small>{s.currency}</small></span>
                {sel && <span className="fp-check">{I.check}</span>}
              </button>
            );
          })}
          {filtered.length > 0 && !!q && filtered.length !== source.length && <div className="fp-count">{filtered.length} / {source.length} tedarikçi</div>}
        </div>
        <footer className="fp-foot">
          <span className="fp-keys"><kbd>↑</kbd><kbd>↓</kbd> gezin · <kbd>Enter</kbd> seç · <kbd>Esc</kbd> vazgeç</span>
          <span className="fp-actions"><button type="button" className="fp-btn" onClick={onClose}>Vazgeç</button></span>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export const SupplierPickerField: React.FC<SupplierPickerFieldProps> = ({ value, onChange, suppliers, placeholder = "Tedarikçi seçin", nullLabel, disabled, invalid, allowClear = true, variant = "form", id, autoFocus, includeInactive, title, onCreate }) => {
  const [open, setOpen] = useState(false);
  const ctl = useRef<HTMLButtonElement>(null);
  const s = value ? suppliers.find((x) => x.supplierId === value) ?? null : null;
  const close = () => { setOpen(false); window.setTimeout(() => ctl.current?.focus(), 0); };
  // Yeni tedarikçi kartına geçişte odak alana geri dönmez; açılan kartın ilk alanı odakta kalır
  const create = onCreate && (() => { setOpen(false); onCreate(); });
  return (
    <div className={`fp-field ${variant} ${disabled ? "disabled" : ""} ${s ? "has" : ""} ${invalid ? "po-invalid" : ""}`}>
      <button ref={ctl} type="button" id={id} className="fp-control" disabled={disabled} autoFocus={autoFocus} aria-haspopup="dialog" aria-expanded={open} title={s ? s.name : undefined} onClick={() => setOpen(true)}>
        {s ? <span className={`fp-mark ${s.isActive ? "" : "off"}`} style={{ ["--fp-hue" as any]: hueOf(s) }}>{firmInitials(s.name)}</span> : <span className="fp-mark null">{I.truck}</span>}
        <span className="fp-text">
          {s ? <><b>{s.name}</b><small>{[s.code, `vade ${s.paymentTermDays} gün`, s.currency].filter(Boolean).join(" · ")}</small></>
            : <b className="fp-placeholder">{nullLabel && !value ? nullLabel : placeholder}</b>}
        </span>
        <span className="fp-chev">{I.chev}</span>
      </button>
      {allowClear && !!value && !disabled && <button type="button" className="fp-clear" aria-label="Seçimi kaldır" title="Seçimi kaldır" onClick={() => onChange(null)}>{I.close}</button>}
      <SupplierPickerModal open={open} onClose={close} suppliers={suppliers} selectedId={value} onSelect={onChange} includeInactive={includeInactive} nullLabel={nullLabel} title={title} onCreate={create} />
    </div>
  );
};

export default SupplierPickerField;
